import { NO_TAB_COUNTS } from '@/components/page-context/context'
import { ACCESS_RESOURCES, type AccessAction, type ResourceId, pickPermissionKey } from '@/config/access-control'
import { type AccessControlInput, useAccessControl } from '@/hooks/access-control'
import { usePageTabCounts } from '@/hooks/usePageTabCounts'
import { cn } from '@/lib/utils'
import { ShieldX } from 'lucide-react'
import { type ReactNode, useEffect, useMemo } from 'react'
import { getRouteAccessDeniedMessage } from './routeAccessMessages'

export interface AccessDeniedFallbackProps {
	title?: string
	message?: string | ReactNode
	actions?: ReactNode
	containerClassName?: string
}

/**
 * Inline "restricted access" state. Mirrors AccessDeniedPage but composes inside
 * partial-access layouts (e.g. tabs where only some subviews are forbidden).
 */
export function AccessDeniedFallback({
	title = 'Access restricted',
	message = 'You do not have permission to view this content. Ask an administrator to grant access.',
	actions,
	containerClassName,
}: AccessDeniedFallbackProps) {
	return (
		<div
			aria-live='polite'
			data-testid='access-restricted'
			className={cn('flex size-full items-center justify-center p-6', containerClassName)}>
			<div className='flex max-w-sm flex-col items-center gap-3 text-center'>
				<span
					className='flex size-10 shrink-0 items-center justify-center rounded-full bg-destructive/10'
					aria-hidden='true'>
					<ShieldX className='size-6 text-destructive' />
				</span>
				<div className='space-y-1'>
					<p className='text-body font-semibold text-foreground'>{title}</p>
					<p className='text-control leading-relaxed text-muted-foreground'>{message}</p>
				</div>
				{actions && <div className='flex gap-2 pt-1'>{actions}</div>}
			</div>
		</div>
	)
}

export interface RouteAccessBoundaryProps extends AccessControlInput {
	children: ReactNode
	title?: string
	message?: string
}

export function RouteAccessBoundary({
	children,
	title,
	message,
	action,
	resource,
	...input
}: RouteAccessBoundaryProps) {
	const isActionExplicit = action !== undefined
	const viewAccess = useAccessControl({ ...input, action: isActionExplicit ? action : 'view', resource })
	const createAccess = useAccessControl({ ...input, resource, action: 'create' })
	const editAccess = useAccessControl({ ...input, resource, action: 'edit' })
	const deleteAccess = useAccessControl({ ...input, resource, action: 'delete' })
	const downloadAccess = useAccessControl({ ...input, resource, action: 'download' })

	const access = useMemo(() => {
		if (isActionExplicit) {
			return viewAccess
		}
		// Only actions the resource actually declares a permission for get a vote. An undeclared slot
		// means "no class-level RBAC required", which useAccessControl reports as allowed, so counting
		// it would hand every role a free pass through the gate.
		const config = resource ? ACCESS_RESOURCES[resource] : undefined
		const declares = (candidate: AccessAction) => pickPermissionKey(config, candidate) !== undefined
		const actions = [
			viewAccess,
			...(declares('create') ? [createAccess] : []),
			...(declares('edit') ? [editAccess] : []),
			...(declares('delete') ? [deleteAccess] : []),
			...(declares('download') ? [downloadAccess] : []),
		]
		const allowedOrWarning = actions.find(a => a.state === 'allowed' || a.state === 'warning')
		if (allowedOrWarning) {
			return allowedOrWarning
		}
		const loading = actions.find(a => a.state === 'loading')
		if (loading) {
			return loading
		}
		return viewAccess
	}, [isActionExplicit, resource, viewAccess, createAccess, editAccess, deleteAccess, downloadAccess])

	const { setTabCounts } = usePageTabCounts()

	useEffect(() => {
		if (access.state === 'denied' && setTabCounts) {
			setTabCounts(NO_TAB_COUNTS)
		}
	}, [access.state, setTabCounts])

	const resolvedMessage =
		message ??
		(resource && access.state === 'denied'
			? getRouteAccessDeniedMessage(resource as ResourceId, access.reason)
			: undefined)

	if (access.state === 'allowed' || access.state === 'warning' || access.state === 'loading') {
		return <>{children}</>
	}

	if (access.state === 'denied') {
		// Same card surface a list page gives its own restricted body through PageContentArea, so the screen
		// looks identical whether the boundary or the page produced it.
		return (
			<div className='flex flex-col gap-y-4 size-full'>
				<div className='bg-card shadow overflow-hidden flex-1 min-h-0'>
					<AccessDeniedFallback title={title} message={resolvedMessage} containerClassName='size-full' />
				</div>
			</div>
		)
	}

	return <>{children}</>
}
